const EVENT_META = {
  CAPITULATION_CONFIRM: {
    labelKey: 'capitulation_confirm',
    defaultLabel: 'Confirmed capitulation',
    defaultDesc: 'Extreme panic with broad decline and high volume',
  },
  PANIC_WITHOUT_VOLUME: {
    labelKey: 'panic_without_volume',
    defaultLabel: 'Panic without volume',
    defaultDesc: 'High panic but volume has not confirmed selling pressure',
  },
  PANIC_REVERSAL_DAY: {
    labelKey: 'panic_reversal_day',
    defaultLabel: 'Panic reversal day',
    defaultDesc: 'Strong panic followed by an intraday rebound',
  },
  FOMO_BROAD: {
    labelKey: 'fomo_broad',
    defaultLabel: 'Broad euphoria',
    defaultDesc: 'Very high excitement with broad market participation',
  },
  FOMO_NARROW_BREADTH: {
    labelKey: 'fomo_narrow_breadth',
    defaultLabel: 'Narrow euphoria',
    defaultDesc: 'High excitement but weak market breadth',
  },
};

const buildThresholdMeta = (eventName) => {
  const panicMatch = eventName.match(/^PANIC_GE_(\d+\.\d+)$/);
  if (panicMatch) {
    const pct = Math.round(parseFloat(panicMatch[1]) * 100);
    return {
      labelKey: 'panic_threshold',
      labelParams: { pct },
      defaultLabel: `Elevated panic (≥${pct}%)`,
      defaultDesc: 'Days when fear score stayed above this level',
      kind: 'panic',
      threshold: parseFloat(panicMatch[1]),
    };
  }

  const fomoMatch = eventName.match(/^FOMO_GE_(\d+\.\d+)$/);
  if (fomoMatch) {
    const pct = Math.round(parseFloat(fomoMatch[1]) * 100);
    return {
      labelKey: 'fomo_threshold',
      labelParams: { pct },
      defaultLabel: `Elevated euphoria (≥${pct}%)`,
      defaultDesc: 'Days when excitement score stayed above this level',
      kind: 'fomo',
      threshold: parseFloat(fomoMatch[1]),
    };
  }

  return {
    labelKey: 'unknown',
    defaultLabel: eventName,
    defaultDesc: 'Historical market behavior pattern',
    kind: 'other',
  };
};

export const getEventMeta = (eventName) => {
  if (EVENT_META[eventName]) {
    return { eventName, ...EVENT_META[eventName] };
  }
  return { eventName, ...buildThresholdMeta(eventName) };
};

const THRESHOLD_EVENTS = [0.99, 0.975, 0.95, 0.90];

const formatThresholdEvent = (prefix, threshold) => `${prefix}_GE_${threshold.toFixed(3)}`;

const getMatchedThresholdEvent = (score, prefix) => {
  const threshold = THRESHOLD_EVENTS.find((value) => score >= value);
  return threshold ? formatThresholdEvent(prefix, threshold) : null;
};

export const isPatternActive = (eventName, signal) => {
  const panic = signal?.panic_score ?? 0;
  const fomo = signal?.fomo_score ?? 0;
  const meta = getEventMeta(eventName);

  if (meta.kind === 'panic' && meta.threshold) {
    return panic >= meta.threshold;
  }
  if (meta.kind === 'fomo' && meta.threshold) {
    return fomo >= meta.threshold;
  }

  if (eventName === 'CAPITULATION_CONFIRM' || eventName === 'PANIC_REVERSAL_DAY' || eventName === 'PANIC_WITHOUT_VOLUME') {
    return panic >= 0.90;
  }
  if (eventName === 'FOMO_BROAD' || eventName === 'FOMO_NARROW_BREADTH') {
    return fomo >= 0.90;
  }

  return false;
};

export const getRelevantEventNames = (signal) => {
  const panic = signal?.panic_score ?? 0;
  const fomo = signal?.fomo_score ?? 0;
  const events = [];

  const panicEvent = getMatchedThresholdEvent(panic, 'PANIC');
  if (panicEvent) {
    events.push(panicEvent);
    if (panic >= 0.95) events.push('CAPITULATION_CONFIRM');
    if (panic >= 0.90) events.push('PANIC_REVERSAL_DAY');
  }

  const fomoEvent = getMatchedThresholdEvent(fomo, 'FOMO');
  if (fomoEvent) {
    events.push(fomoEvent);
    if (fomo >= 0.95) events.push('FOMO_BROAD');
    if (fomo >= 0.90) events.push('FOMO_NARROW_BREADTH');
  }

  // Cautious / moderate market: show nearest reference pattern for the dominant signal
  if (events.length === 0) {
    if (panic >= fomo && panic >= 0.45) {
      events.push('PANIC_GE_0.900');
    } else if (fomo > panic && fomo >= 0.45) {
      events.push('FOMO_GE_0.900');
    }
  }

  return [...new Set(events)].slice(0, 2);
};

export const getEventLabel = (eventName, t) => {
  const meta = getEventMeta(eventName);
  return t(`sentiment.behavior.events.patterns.${meta.labelKey}`, {
    defaultValue: meta.defaultLabel,
    ...(meta.labelParams || {}),
  });
};

export const getEventDescription = (eventName, t) => {
  const meta = getEventMeta(eventName);
  return t(`sentiment.behavior.events.patterns.${meta.labelKey}_desc`, {
    defaultValue: meta.defaultDesc,
    ...(meta.labelParams || {}),
  });
};

export const getPatternTone = (eventName) => {
  const meta = getEventMeta(eventName);
  if (meta.kind === 'panic' || eventName.startsWith('PANIC')) {
    return { accent: '#e65100', surface: '#fff8f0' };
  }
  if (meta.kind === 'fomo' || eventName.startsWith('FOMO')) {
    return { accent: '#00838f', surface: '#f0fbfc' };
  }
  return { accent: '#475569', surface: '#f8fafc' };
};

export const getPatternOutcome = (event, t) => {
  const winRate = (event.win_rate ?? 0) * 100;
  const median = (event.median_return ?? 0) * 100;

  if (winRate >= 55 && median > 0) {
    return {
      label: t('sentiment.behavior.events.outcome_positive', 'Selective accumulation'),
      color: '#2e7d32',
      bg: '#e8f5e9',
    };
  }

  if (winRate < 45 || median < 0) {
    return {
      label: t('sentiment.behavior.events.outcome_caution', 'Stay cautious'),
      color: '#c62828',
      bg: '#ffebee',
    };
  }

  return {
    label: t('sentiment.behavior.events.outcome_neutral', 'Trade selectively'),
    color: '#e65100',
    bg: '#fff3e0',
  };
};

export const buildPatternTakeaway = (event, t) => {
  const winRate = (event.win_rate ?? 0) * 100;
  const median = (event.median_return ?? 0) * 100;
  const days = event.horizon_days ?? 20;
  const count = event.count ?? 0;
  const medianText = `${median >= 0 ? '+' : ''}${median.toFixed(2)}`;
  const params = {
    days,
    count,
    winRate: winRate.toFixed(0),
    median: medianText,
  };

  if (winRate >= 55 && median > 0) {
    return t('sentiment.behavior.events.takeaway_positive', {
      ...params,
      defaultValue: `In ${count} similar cases, ${winRate.toFixed(0)}% of the time VNINDEX was higher after ${days} days, with a typical return of ${medianText}%.`,
    });
  }

  if (winRate < 45 || median < 0) {
    return t('sentiment.behavior.events.takeaway_caution', {
      ...params,
      defaultValue: `In ${count} similar cases, only ${winRate.toFixed(0)}% led to gains after ${days} days. Typical return was ${medianText}% — stay selective.`,
    });
  }

  return t('sentiment.behavior.events.takeaway_neutral', {
    ...params,
    defaultValue: `In ${count} similar cases, outcomes were mixed: ${winRate.toFixed(0)}% positive after ${days} days, typical return ${medianText}%.`,
  });
};
